import { Metamodel } from '../base/model';
import { AdapterRegistry } from './adapter-registry';
import { NamedAdapter } from './named.adapter';
import { ManagedPrivateEndpoint } from '../managedprivateendpoint/managedprivateendpoint';
import { SharedModel } from '../sharedmodel';
import { ProvisioningState, ConnectionState, PrivateEndpointType } from '../managedprivateendpoint/managedprivateenpoint-types';
import { ManagedVirtualNetwork } from '../managedvirtualnetwork/managedVirtualNetwork';
import { ReferenceAdapter } from './reference.adapter';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(ManagedPrivateEndpoint),
    adapts: {
        name: '*',
        properties: '*'
    }
})
export class ManagedPrivateEndpointAdapter extends NamedAdapter<ManagedPrivateEndpoint, SharedModel.ManagedPrivateEndpointResource> {
    protected _toResource(model: ManagedPrivateEndpoint): SharedModel.ManagedPrivateEndpointResource {
        const resource = super._toResource(model) as SharedModel.ManagedPrivateEndpointResource;
        resource.properties = {
            privateLinkResourceId: model.privateLinkResourceId,
            groupId: model.groupId,
            fqdns: (model.fqdns && model.fqdns.length) ? [...model.fqdns] : undefined
        };

        return resource;
    }

    protected _toModel(resource: SharedModel.ManagedPrivateEndpointResource, model?: ManagedPrivateEndpoint): ManagedPrivateEndpoint {
        if (!model) {
            model = ManagedPrivateEndpoint.MODEL.create<ManagedPrivateEndpoint>();
        }
        super._toModel(resource, model);
        model.error = resource.properties.error;
        model.groupId = resource.properties.groupId as PrivateEndpointType;
        model.ipAddress = resource.properties.ipAddress;
        model.resourceId = resource.properties.resourceId;
        model.privateLinkResourceId = resource.properties.privateLinkResourceId;
        model.provisioningState = this._convertProvisioningState(resource.properties.provisioningState);
        model.isReserved = resource.properties.isReserved;
        model.resourceName = model.privateLinkResourceId.substr(model.privateLinkResourceId.lastIndexOf('/') + 1);

        if (resource.properties.connectionState) {
            model.connectionState = {
                description: resource.properties.connectionState.description,
                status: this._convertApprovalState(resource.properties.connectionState.status)
            };
        }

        if (resource.properties.managedVirtualNetwork) {
            model.managedVirtualNetwork = ReferenceAdapter.toProxyModel<ManagedVirtualNetwork>(
                Metamodel.modelFor(ManagedVirtualNetwork),
                resource.properties.managedVirtualNetwork, this.context);
        }

        if (resource.properties.fqdns) {
            model.fqdns = [...resource.properties.fqdns];
        }

        return model;
    }

    private _convertProvisioningState(state: string): ProvisioningState {
        state = (state || '').toLowerCase();
        switch (state) {
            case 'deleting':
                return ProvisioningState.Deleting;
            case 'failed':
                return ProvisioningState.Failed;
            case 'succeeded':
                return ProvisioningState.Succeeded;
            case 'updating':
                return ProvisioningState.Updating;
            case 'creating':
                return ProvisioningState.Creating;
            case 'provisioning':
                return ProvisioningState.Provisioning;
            default:
                return undefined;
        }
    }

    private _convertApprovalState(state: string): ConnectionState {
        state = (state || '').toLowerCase();
        switch (state) {
            case 'pending':
                return ConnectionState.Pending;
            case 'approved':
                return ConnectionState.Approved;
            case 'rejected':
                return ConnectionState.Rejected;
            case 'disconnected':
                return ConnectionState.Disconnected;
            default:
                return undefined;
        }
    }
}
